import chroma from "chroma-js";

import { zoomIn } from "utils/animations";

// Helpers
class Px {
  constructor(number) {
    this.number = number;
  }

  toString() {
    return `${this.number}px`;
  }
}
const px = number => new Px(number);

const hover = color => chroma.mix(color, "#000", 0.2);

/* colors (from the design team's brand.ai library) */

// Primary Colors

const GREEN_MEADOW = "#21ba45";
const BLUE = "#348db0";
const CLOUDY_BLUE = "#04C9D7";
const GREEN = "#6db359";
const ORANGE = "#f99d1c";
const AWS_ORANGE = "#F58245";
const RED = "#ea4c3b";
const YELLOW = "#fac519";
const WHITE = "#fff";
const TEAL = "#00B09F";
const LIGHT_GREY_1 = "#fafafa";
const LIGHT_GREY_2 = "#f2f2f2";
const LIGHT_GREY_3 = "#e5e5e5";
const LIGHT_GREY_4 = "#ddd";
const LIGHT_GREY_5 = "#c5c5c5";
const LIGHT_GREY_6 = "#f4f4f4";
const MERCURY_GREY = "#e2e2e2";
const DUSTY_GREY = "#999999";
const SILVER = "#bcbcbc";
const MID_GREY_1 = "#999";
const MID_GREY_2 = "#7a7a7a";
const MID_GREY_3 = "rgba(34, 36, 38, 0.15)";
const MID_GREY_4 = "#707070";
const DARK_GREY_1 = "#414141";
const DARK_GREY_2 = "#2d2d2d";
const DARK_GREY_3 = "#4a4a4a";
const DARK_GREY_4 = "#666666";
const BLACK = "#000";

// Brand Colors
const SWI_ORANGE = "#f99d1c";
const LIBRATO_BLUE = "#0f80aa";
const LOGGLY_RED = "#FF3A00";
const PAPERTRAIL_BLUE = "#04C9D7";
const PINGDOM_YELLOW = "#FFF300";

// Data Viz Colors
const BOSTON_BLUE = "#398DAE";
const TREE_POPPY_ORANGE = "#FD9437";
const PATINA_GREEN = "#5C9C8D";
const CINNABAR_RED = "#EA4C3B";
const JAGGED_ICE = "#D6E8EF";
const JAFFA_ORANGE = "#F28D39";

//ChartColors
const FERN_GREEN = "#73b06e";
const JAPONICA_RED = "#db6c6e";
const SAN_MARIO_BLUE = "#3d70ad";
const AQUA_ISLAND = "#9ac7d8";

// Trace Summary Colors
const WISTERIA_PURPLE = "#a169b0";
const CHESTNUT_ROSE = "#cb5252";
const RUSTY_RED = "#9e5048";
const PURPLE = "#554E99";
const TABLE_HEADER_GREY = "#f9fafb";

// Heatmap
const REQUESTS_START = "#eaf3f7";
const REQUESTS_END = BLUE;

const QUERIES_START = "#fcf2e7";
const QUERIES_END = "#e68417";

const REMOTE_START = "#e7f7f5";
const REMOTE_END = "#18af9f";

const CACHES_START = "#e9eef4";
const CACHES_END = "#235a94";

const EXCEPTIONS_START = "#fbe0dd";
const EXCEPTIONS_END = "#ed5543";

// Cloud Colors
const GAINSBORO = "#dedede";

/* exports */
export const COLORS = {
  MAIN: BLUE,
  BACKGROUND: WHITE,
  BLUE,
  GREEN,
  ORANGE,
  AWS_ORANGE,
  RED,
  YELLOW,
  WHITE,
  TEAL,
  LIGHT_GREY_1,
  LIGHT_GREY_2,
  LIGHT_GREY_3,
  LIGHT_GREY_4,
  LIGHT_GREY_5,
  LIGHT_GREY_6,
  MERCURY_GREY,
  DUSTY_GREY,
  SILVER,
  MID_GREY_1,
  MID_GREY_2,
  DARK_GREY_1,
  DARK_GREY_2,
  BLACK,
  SWI_ORANGE,
  LIBRATO_BLUE,
  BOSTON_BLUE,
  PATINA_GREEN,
  TREE_POPPY_ORANGE,
  CINNABAR_RED,
  JAGGED_ICE,
  JAFFA_ORANGE,
  FERN_GREEN,
  JAPONICA_RED,
  SAN_MARIO_BLUE,
  AQUA_ISLAND,
  WISTERIA_PURPLE,
  CHESTNUT_ROSE,
  RUSTY_RED,
  PURPLE,
  REQUESTS_START,
  REQUESTS_END,
  REMOTE_START,
  REMOTE_END,
  QUERIES_START,
  QUERIES_END,
  CACHES_START,
  CACHES_END,
  EXCEPTIONS_START,
  EXCEPTIONS_END,
};
export const CUSTOM_THRESHOLD_COLORS = [
  GREEN,
  YELLOW,
  JAFFA_ORANGE,
  CINNABAR_RED,
];

// Light theme

export const lightTheme = {
  // General theming. Should be pretty generic
  danger: RED,
  warning: ORANGE,
  background: LIGHT_GREY_3,
  disabled: LIGHT_GREY_4,
  border: `1px solid ${LIGHT_GREY_4}`,
  boxShadow: "rgba(0, 0, 0, 0.15)",
  fontFamily: "'Open Sans', sans-serif",
  fontFamilyMono: "Courier, monospace",
  fontSize: "13px",
  fontEmphasis: "600",
  lineHeight: 1.3,
  margin: "10px",
  marginHalf: "5px",
  padding: px(20),
  paddingDefault: "14",
  text: DARK_GREY_1,
  textLight: MID_GREY_2,
  unitColor: MID_GREY_1,

  empty: WHITE,
  error: RED,
  success: GREEN,
  fade50: "rgba(255, 255, 255, 0.5)",
  label: MID_GREY_1,
  link: BLUE,
  linkHover: hover(BLUE),
  content: {
    backgroundColor: WHITE,
    color: DARK_GREY_1,
    secondaryColor: MID_GREY_2,
  },

  breakPoints: {
    mobile: "800px",
  },

  zIndexes: {
    topNavBar: 30,
    dropDown: 25,
    tooltip: 25,
    subNavBar: 25,
    filterBar: 20,
    card: 20,
  },

  Collapsible: {
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 20,
    indent: 18,
    fontSize: 15,
    iconSize: 14,
    height: 43,
  },

  Accordion: {
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 10,
    indent: 15,
    fontSize: 13,
    iconSize: 11,
  },

  FunctionProfiling: {
    section: {
      paddingLeft: 37,
      indent: 15,
    },
  },
  cloudComponents: {
    borderColor: GAINSBORO,
    typography: {
      fontSize: "14px",
      fontWeight: "400",
      bold: "600",
    },
    textarea: {
      placeholder: GAINSBORO,
      borderFocus: "#85b7d9",
    },
    tabs: {
      tabsBorder: GAINSBORO,
      fontColor: DARK_GREY_1,
      activeColor: SWI_ORANGE,
      hoverBackground: LIGHT_GREY_2,
      border: `2px solid ${GAINSBORO}`,
      padding: "15px",
    },
    button: {
      hoverBackground: LIGHT_GREY_2,
    },
    filters: {
      fontColor: DARK_GREY_1,
      activeColor: SWI_ORANGE,
      hoverBackground: LIGHT_GREY_2,
      background: WHITE,
    },
    sidePanel: {
      width: "640px",
      padding: "20px",
      header: {
        background: MID_GREY_4,
        color: ORANGE,
        height: "50px",
        iconSize: "40px",
      },
    },

    icon_blue: BLUE,
  },
  Infrastructure: {
    host: {
      fillNormal: "#3C6F88",
      fillHover: "#71A5BE",
      fillEmpty: LIGHT_GREY_4,
      stroke: "#E3E3E3",
      border: "1px solid white",
    },

    hostView: {
      iconColor: WHITE,
      cardBackground: WHITE,
      dropdownBackground: WHITE,
      dropdownBorderColor: BLACK,
      dropdownHeaderBorderColor: GAINSBORO,
      cardBorder: LIGHT_GREY_4,
      chartBorderFocused: MID_GREY_1,
      tableBorder: MERCURY_GREY,
      subheaderColor: "rgba(0, 0, 0, 0.6)",
      chartExpansionBackground: LIGHT_GREY_2,
      descriptionColor: DUSTY_GREY,
      fontColor: BLACK,
      orangePill: ORANGE,
      pageBackground: WHITE,
      secondaryFontColor: SILVER,
      tabsHeaderBackground: WHITE,
    },

    tooltip: {
      textColor: WHITE,
      kpiTextColor: LIGHT_GREY_4,
      kpiUnitColor: MID_GREY_1,
      textFill: DARK_GREY_1,
      arrowColor: MID_GREY_2,
      backgroundColor: "rgba(45, 45, 45, 0.98)",
    },

    pagination: {
      active: ORANGE,
      disabled: LIGHT_GREY_4,
    },

    fillBy: {
      backgroundColor: LIGHT_GREY_1,
      labelColor: "#333333",
      rangeMin: JAGGED_ICE,
      rangeMax: BLUE,
    },

    dropdown: {
      color: DARK_GREY_1,
      colorOnHover: WHITE,
      backgroundOnHover: BLUE,
      elementLineHeight: 13,
      elementPadding: 10,
      menuItem: {
        backgroundColor: hover(BLUE),
      },
    },
  },

  // Component specific styles, key should match component name
  Auth: {
    background: WHITE,
  },

  PageContent: {
    padding: "30px",
  },

  Panel: {
    background: LIGHT_GREY_1,
    headerHeight: "45px",
  },

  EventTable: {
    color: DARK_GREY_1,
    background: WHITE,
    Backtrace: {
      color: DARK_GREY_2,
    },
  },

  Pill: {
    default: MID_GREY_2,
    text: WHITE,
    inactive: LIGHT_GREY_2,
    inactiveText: MID_GREY_2,
    loadingIcon: WHITE,
  },

  Alert: {
    color: DARK_GREY_1,
    backgroundEven: LIGHT_GREY_2,
    background: WHITE,
    descriptionColor: "#858585",
    statusTriggered: RED,
    statusRecent: ORANGE,
  },
  EmptyState: {
    descriptionColor: DARK_GREY_4,
  },

  Dashboards: {
    active: ORANGE,
    inactive: MID_GREY_2,
    activeTab: WHITE,
    inactiveTab: LIGHT_GREY_2,
  },

  herokuBannerHeight: "32px",
  herokuHeaderOffset: "35px",
  herokuHeaderPopupOffset: "37px",
  TopNav: {
    backgroundColor: DARK_GREY_2,
    height: "63px",
    heightWithHerokuBanner: "93px", // herokuBannerHeight + TopNav.height
    oldHeight: "50px",
    oldHeightWithHerokuBanner: "82px",

    logo: {
      height: "28px",
    },
  },
  Breadcrumb: {
    color: WHITE,
    fontSize: "16px",
    fontWeight: 400,
    height: "33px",
  },
  NavDivider: {
    borderColor: DARK_GREY_1,
  },
  SubNav: {
    backgroundColor: WHITE,
    color: DARK_GREY_1,
    height: "46px",
    padding: "20px",
    margin: "22px",
    active: {
      color: ORANGE,
    },
  },
  SideNav: {
    backgroundColor: DARK_GREY_1,
    width: px(60),
    expanded: {
      width: px(300),
    },
    oldWidth: px(50),
  },

  UserDropdown: {
    background: LIGHT_GREY_2,
    item: {
      border: `1px solid ${LIGHT_GREY_4}`,
    },
  },

  Button: {
    backgroundColor: WHITE,
    border: `1px solid ${BLUE}`,
    color: BLUE,
    height: "35px",
    hover: {
      backgroundColor: hover(BLUE),
      color: WHITE,
    },
  },

  PrimaryButton: {
    backgroundColor: BLUE,
    color: WHITE,
    hover: {
      backgroundColor: hover(BLUE),
      color: WHITE,
    },
  },

  ActionButton: {
    color: WHITE,
    background: RED,
    hover: {
      background: hover(RED),
    },
  },

  WarningButton: {
    background: ORANGE,
    hover: {
      background: hover(ORANGE),
    },
  },

  DangerButton: {
    backgroundColor: WHITE,
    borderColor: RED,
    color: RED,
    hover: {
      backgroundColor: hover(RED),
      color: WHITE,
    },
  },

  AltPrimaryButton: {
    backgroundColor: GREEN,
    borderColor: GREEN,
    color: WHITE,
    hover: {
      backgroundColor: hover(GREEN),
      color: WHITE,
    },
  },

  RoundButton: {
    color: ORANGE,
    backgroundColor: WHITE,
    borderColor: LIGHT_GREY_4,
  },

  TransparentButton: {
    hover: {
      backgroundColor: hover(WHITE),
    },
  },

  TextInput: {
    border: `1px solid ${LIGHT_GREY_4}`,
    inactive: LIGHT_GREY_1,
    focus: {
      borderColor: BLUE,
    },
    placeholder: MID_GREY_1,
  },

  Radio: {
    size: 19,
    checked: {
      backgroundColor: BLUE,
    },
    disabled: {
      backgroundColor: LIGHT_GREY_2,
      checked: {
        backgroundColor: LIGHT_GREY_4,
      },
    },
  },

  Checkbox: {
    size: 19,
    border: `1px solid ${LIGHT_GREY_4}`,
    focus: {
      borderColor: LIGHT_GREY_4,
    },
    checked: {
      borderColor: BLUE,
    },
  },

  Label: {
    color: DARK_GREY_1,
  },

  ToggleSwitch: {
    backgroundColor: LIGHT_GREY_5,
    color: WHITE,
    size: 19,
    padding: 2,
    checked: {
      backgroundColor: GREEN,
    },
  },

  Modal: {
    header: {
      backgroundColor: BLUE,
      color: WHITE,
      fontSize: "15px",
      height: "45px",
      padding: "10px 20px",
    },
    content: {
      padding: "30px",
    },
    footer: {},
  },

  ConfirmationModal: {
    backgroundColor: LIGHT_GREY_2,
  },

  SidePanelLink: {
    color: BLUE,
  },

  SidePanel: {
    header: {
      backgroundColor: BLUE,
      color: WHITE,
      fontSize: "17px",
      fontWeight: 200,
    },
  },

  Exceptions: {
    sidePanel: {
      dataColor: BLUE,
      borderColor: LIGHT_GREY_4,
      backtraces: {
        titleColor: BLUE,
        labelColor: MID_GREY_1,
      },
    },
  },

  Alerts: {
    borderColor: MERCURY_GREY,
    tooltipIconColor: BLUE,
    icons: {
      firing: RED,
      resolved: YELLOW,
      enabled: GREEN,
      disabled: LIGHT_GREY_4,
      disabledMark: MID_GREY_1,
    },

    steps: {
      tileBorder: GAINSBORO,
      tileHoverBorder: "#b2b2b2",
      activeTileBackground: BLUE,
      iconBackground: "#e9f4f7",
      activeIconBackground: "#40a5c2",
      iconFill: BLUE,
      activeIconFill: WHITE,
      headingColor: "#212121",
      textColor: "#666666",
      activeTextColor: WHITE,
    },
  },

  Plugins: {
    loggly: "#ff3901",
    papertrail: "#065199",
  },

  CloudUi: {
    segmentBorder: `1px solid ${MID_GREY_3}`,
    borderColor: MID_GREY_3,
    borderRadius: "3px",
    segmentShadow: `0 1px 2px 0 ${MID_GREY_3}`,
    primaryButtonColor: "#e3e4e6",
    divider: "1px 0 0 0 #f7f7f7",
    tooltip: {
      font: "12px",
      titleColor: BLUE,
    },
    iconButton: {
      width: "38px",
      height: "36px",
    },
  },

  OnboardingModal: {
    backgroundColor: LIGHT_GREY_2,
    headerBackground: WHITE,
    border: `1px solid ${LIGHT_GREY_4}`,
    margin: "60px auto",
    color: MID_GREY_4,

    h1: {
      color: DARK_GREY_1,
      fontSize: "32px",
      fontWeight: 300,
    },
    h2: {
      color: MID_GREY_1,
      fontSize: "16px",
      fontWeight: "normal",
      lineHeight: "26px",
    },
    expanded: {
      color: MID_GREY_1,
      h1: {
        color: WHITE,
        fontSize: "32px",
        fontWeight: 300,
      },
      h2: {
        color: MID_GREY_1,
        fontSize: "16px",
        fontWeight: "normal",
      },
      h3: {
        color: WHITE,
        fontSize: "21px",
        fontWeight: "normal",
      },
    },
    tall: {
      margin: "40px auto",
    },
    Headline: {
      color: DARK_GREY_2,
      fontSize: "17px",
    },
    StepNumber: {
      backgroundColor: BLUE,
      color: WHITE,
      fontSize: "13px",
      fontWeight: "bold",
      marginRight: "15px",
      diameter: "25px",
    },
    NumberedStepContent: {
      marginLeft: "40px", // === StepNumber.diameter + StepNumber.marginRight
    },
    footer: {
      backgroundColor: WHITE,
      color: MID_GREY_1,
    },
    help: {
      color: MID_GREY_1,
      fontSize: "11px",
      lineHeight: "21px",
      padding: "15px",
    },
    InviteMembers: {
      successBackgroundColor: BLUE,
      Avatar: {
        colors: {
          first: BLUE,
          second: GREEN,
          third: ORANGE,
        },
      },
      AdminLabel: {
        color: MID_GREY_2,
        fontSize: "11px",
        lineHeight: "13px",
      },
    },
  },

  OnboardingProgressBar: {
    fontSize: "13px",
    crumb: {
      color: WHITE,

      disabled: {
        opacity: 0.5,
      },
      circle: {
        size: "16px",
      },
    },
  },

  OnboardingPartner: {
    backgroundColor: WHITE,
  },

  OptionToggleButton: {
    backgroundColor: LIGHT_GREY_3,
    color: DARK_GREY_1,
    hover: {
      backgroundColor: LIGHT_GREY_4,
    },
    active: {
      backgroundColor: BLUE,
      color: WHITE,
      hover: {
        backgroundColor: hover(BLUE),
      },
    },
    subtitle: {
      color: MID_GREY_2,
    },
  },

  Table: {
    border: `1px solid ${LIGHT_GREY_4}`,
    borderColor: LIGHT_GREY_4,
    fixedColumnWidth: "20%",
    horizontalBarColor: AQUA_ISLAND,
  },

  TableRow: {
    height: "43px",
    even: {
      backgroundColor: LIGHT_GREY_2,
    },
    odd: {
      backgroundColor: LIGHT_GREY_1,
    },
    hover: {
      backgroundColor: WHITE,
    },
  },

  TableHeader: {
    color: ORANGE,
    height: "31px",
    fontSize: "11px",
    fontWeight: 600,
  },

  TableCell: {
    backgroundColor: "transparent",
    sorted: {
      backgroundColor: "rgba(255,255,255, .5)",
    },
  },

  Sparkline: {
    color: LIBRATO_BLUE,
    height: 40,
    error: {
      color: RED,
    },
    TableRow: {
      height: "65px",
    },
  },

  Heatmap: {
    height: px(200),
    padding: px(30),

    leftAxis: {
      width: px(60),
    },
  },

  KpiSet: {
    borderColor: LIGHT_GREY_4,
    backgroundColor: WHITE,
    backgroundColorDark: BLUE,
  },

  KpiNotAvailable: {
    color: MID_GREY_2,
  },

  KpiNoErrorsReported: {
    color: GREEN_MEADOW,
  },

  ExceptionRow: {
    backgroundColor: WHITE,
    borderColor: SILVER,
  },

  Tooltip: {
    // TODO: standardize these?
    backgroundColor: "#fffece",
    color: "#333",
  },

  PaymentMethod: {
    borderColor: LIGHT_GREY_4,
    backgroundColor: LIGHT_GREY_1,
    backgroundColorLight: WHITE,
    p: {
      color: DARK_GREY_1,
    },
    label: {
      color: MID_GREY_1,
    },
    span: {
      color: DARK_GREY_2,
    },
    InputErrors: {
      fontSize: "11px",
      height: "13px",
      iconSize: "12.5px",
    },
  },

  TrialBanner: {
    banner: {
      backgroundColor: WHITE,
      backgroundColorDark: LIGHT_GREY_2,
    },
    progress: {
      color: {
        remaining: LIGHT_GREY_4,
      },
    },
    p: {
      color: DARK_GREY_1,
    },
  },

  TrialModal: {
    backgroundColor: LIGHT_GREY_1,
    header: {
      backgroundColor: DARK_GREY_1,
      color: "#FFFFFF",
      fontSize: "18px",
    },
    smallHeader: {
      fontSize: "16px",
      color: DARK_GREY_1,
      lighterColor: MID_GREY_2,
    },
    avatar: {
      size: "40px",
      backgroundColor: SWI_ORANGE,
      color: "#FFFFFF",
      fontSize: "20px",
    },
    user: {
      higlightFont: {
        fontWeight: "600",
        color: WHITE,
      },
      fontSize: "12px",
    },
  },

  PaymentFailedModal: {
    smallHeader: {
      fontSize: "16px",
      color: DARK_GREY_1,
      lighterColor: MID_GREY_2,
    },
    header: {
      fontSize: "18px",
      color: DARK_GREY_1,
      lighterColor: MID_GREY_2,
    },
  },

  Tab: {
    active: {
      backgroundColor: BLUE,
      color: WHITE,
    },
    inactive: {
      backgroundColor: WHITE,
      borderColor: LIGHT_GREY_4,
      color: BLUE,
    },

    plain: {
      backgroundColor: WHITE,
      color: DARK_GREY_1,
      active: {
        borderColor: MID_GREY_1,
      },
      inactive: {
        borderColor: LIGHT_GREY_3,
      },
    },
  },

  Usage: {
    label: {
      fontSize: "11px",
    },
    Header: {
      backgroundColor: LIGHT_GREY_2,
      fontSize: "17px",
    },
    Widget: {
      backgroundColor: WHITE,
      border: `1px solid ${LIGHT_GREY_4}`,
      Price: {
        color: MID_GREY_2,
        label: {
          color: LIGHT_GREY_5,
        },
      },
      BillingCycle: {
        backgroundColor: LIGHT_GREY_3,
        label: {
          color: MID_GREY_2,
        },
      },
    },
    WidgetCost: {
      backgroundColor: LIGHT_GREY_1,
    },
    CostLine: {
      fontSize: "15px",
    },
    CustomMetricsCount: {
      color: LIGHT_GREY_5,
      excess: RED,
      fontSize: "51px",
    },
  },

  ContractDetails: {
    borderRadius: "3px",
    dividerColor: LIGHT_GREY_4,
    margin: "15px",
    padding: "30px",
    activeTabColor: WHITE,
    Widget: {
      backgroundColor: WHITE,
      border: `1px solid ${MID_GREY_3}`,
      boxShadow: `0 1px 2px 0 rgba(0, 0, 0, 0.05)`,
      color: BLACK,
      fontSize: "14px",
    },
    ListLine: {
      color: DARK_GREY_1,
      lineHeight: "20px",
    },
    ListHeading: {
      fontSize: "16px",
      fontWeight: "600",
      lineHeight: "24px",
      color: MID_GREY_2,
    },
    Heading: {
      fontSize: "18px",
      fontWeight: 600,
      highlightColor: SWI_ORANGE,
      color: MID_GREY_2,
    },
    PackHeading: {
      fontSize: "24px",
      fontWeight: "normal",
    },
    Price: {
      fontSize: "24px",
      color: SWI_ORANGE,
      darkColor: MID_GREY_2,
    },
    Emphasis: {
      color: BLACK,
      fontWeight: 600,
    },
    tinyFontSize: "12px",
    Logo: {
      fontSize: "32px",
      fontWeight: "normal",
      lineHeight: "48px",
    },
    MenuButton: {
      color: DARK_GREY_1,
      backgroundColor: "transparent",
      hover: {
        backgroundColor: LIGHT_GREY_2,
      },
    },
  },

  Bookmark: {
    disabled: chroma(DARK_GREY_1)
      .alpha(0.3)
      .css(),
    default: chroma(DARK_GREY_1)
      .alpha(0.8)
      .css(),
    hover: DARK_GREY_1,
    active: SWI_ORANGE,
    homepage: DARK_GREY_1,
    old: {
      default: "#D0D0D0",
      hover: "#FFFFFF",
    },
  },

  SdkChart: {
    compactPadding: "10px 10px 0",
    defaultChartHeight: "150px",
  },

  Agents: {
    host: {
      backgroundColor: DARK_GREY_1,
    },
    cloudwatch: {
      backgroundColor: AWS_ORANGE,
    },
    apm: {
      backgroundColor: TEAL,
    },
    disabled: {
      backgroundColor: LIGHT_GREY_4,
    },
  },

  Activation: {
    backgroundColor: LIGHT_GREY_6,

    header: {
      backgroundColor: BLACK,
      color: WHITE,
      fontSize: "14px",
      buttonColor: "#333333",
    },

    content: {
      backgroundColor: WHITE,
      copyColor: "#4A4A4A",
      anchorColor: BLUE,
      featureCopyColor: "#515151",
      featureTitleColor: "#5c5d5d",
      circleBorder: `2px solid ${BLACK}`,
      circleBackgroundColor: BLACK,

      title: {
        color: DARK_GREY_1,
        fontSize: "32px",
        letterSpacing: "0.57px",
        lineHeight: "38px",
      },
    },

    footer: {
      backgroundColor: LIGHT_GREY_6,
      copyColor: "#5c5d5d",
    },

    organizationSelection: {
      dividerColor: DUSTY_GREY,
      buttonBackgroundColor: WHITE,
      buttonColor: "#666666",
      buttonHoverBackgroundColor: LIGHT_GREY_1,
    },
  },

  ActivationError: {
    backgroundColor: WHITE,
    copyColor: DARK_GREY_1,
  },

  Environments: {
    disabledColor: MID_GREY_2,
    fontSize: "11px",
  },

  Services: {
    content: {
      topNavHeight: px(66),
    },
  },

  Organization: {
    appoptics: BLUE,
    loggly: LOGGLY_RED,
    papertrail: PAPERTRAIL_BLUE,
    pingdom: PINGDOM_YELLOW,
    productCircleBorder: "1px solid #dddddd",
  },

  Traces: {
    subHeader: MID_GREY_2,
    breakdownLabelTextColor: WHITE,
    labelTextColor: WHITE,
    labelFontSize: "11px",
    labelHeight: "15px",
    labelDefaultColor: LIGHT_GREY_4,
    unitColor: MID_GREY_1,
    percentColor: MID_GREY_1,
    lightTextColor: DARK_GREY_1,
    textColor: DARK_GREY_3,
    bannerBackground: WHITE,
    disabledBannerBackground: LIGHT_GREY_6,
    disabledBannerBorder: `1px solid ${LIGHT_GREY_4}`,
    tableHeaderBackground: TABLE_HEADER_GREY,
    tooltipBackground: WHITE,
    tooltipBorder: LIGHT_GREY_4,
    tooltipText: MID_GREY_2,
  },

  animations: {
    zoomIn,
  },
};

export const ssaTheme = {
  link: CLOUDY_BLUE,
  linkHover: hover(CLOUDY_BLUE),
  Button: {
    ...lightTheme.Button,
    backgroundColor: SWI_ORANGE,
    border: "none",
    borderRadius: "3px",
    color: WHITE,
    height: "40px",
    hover: {
      backgroundColor: hover(SWI_ORANGE),
      color: WHITE,
    },
  },
  Checkbox: {
    ...lightTheme.Checkbox,
    borderRadius: "3px",
    checked: {
      borderColor: LIGHT_GREY_4,
    },
  },
  TextInput: {
    ...lightTheme.TextInput,
    borderRadius: "3px",
    focus: {
      borderColor: LIGHT_GREY_4,
    },
  },
  Modal: {
    content: {
      ...lightTheme.Modal.content,
      borderRadius: "0 0 5px 5px",
    },
    header: {
      backgroundColor: SWI_ORANGE,
      borderRadius: "5px 5px 0 0",
      color: WHITE,
      fontSize: "16px",
      height: "50px",
      padding: "0 20px",
    },
    footer: {
      backgroundColor: LIGHT_GREY_1,
      border: "none",
      padding: lightTheme.padding,
    },
  },
  ConfirmationModal: {
    backgroundColor: WHITE,
  },
};

export default lightTheme;
